import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, getFirestore, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { compareUnitNumbers } from '../../utils/unitSort';
import { parseDateLike, toMs, type BankTransactionRow } from './collections';
import { buildTodaySummary, type TodayBookingInfo, type TodayChangeOrder, type TodayMaterialOrder, type TodayUnit } from './summary';
import { buildCostSummary, type CostBill, type CostEmployee } from './costs';
import { normalizeDefinitionSteps, type WorkflowCase, type WorkflowDefinitionStep } from './workflowBoard';

const SALE_DEED_UPLOAD_IDS = ['sale_deed', 'sale_deed_upload', 'sale_deed_signed'];

// Same field lookups the Reports page uses: top-level first, then any step's saved data.
const fieldFromBooking = (booking: any, keys: string[]): any => {
  if (!booking || typeof booking !== 'object') return null;
  for (const key of keys) if (booking[key]) return booking[key];
  const stepData = booking.stepData;
  if (stepData && typeof stepData === 'object') {
    for (const entry of Object.values(stepData as Record<string, any>)) {
      if (!entry || typeof entry !== 'object') continue;
      for (const key of keys) if ((entry as any)[key]) return (entry as any)[key];
    }
  }
  return null;
};

const customerNameOf = (c: any): string =>
  String(
    c.displayName || c.fullName || c.customerName || c.allotteeName || c.name ||
      [c.firstName, c.middleName, c.surname || c.lastName].filter(Boolean).join(' ')
  ).trim();

export function useTodayData(projectId: string | null, includeForms = false) {
  const db = useMemo(() => getFirestore(), []);
  const [units, setUnits] = useState<TodayUnit[]>([]);
  const [txns, setTxns] = useState<BankTransactionRow[]>([]);
  const [changeOrders, setChangeOrders] = useState<TodayChangeOrder[]>([]);
  const [projectMeta, setProjectMeta] = useState<Record<string, any> | null>(null);
  const [bookingInfoByUnitId, setBookingInfoByUnitId] = useState<Map<string, TodayBookingInfo>>(new Map());
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [txnsLoaded, setTxnsLoaded] = useState(false);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<TodayMaterialOrder[]>([]);
  const [bills, setBills] = useState<CostBill[]>([]);
  const [employees, setEmployees] = useState<CostEmployee[]>([]);
  const [mainSteps, setMainSteps] = useState<WorkflowDefinitionStep[]>([]);
  const [loanSteps, setLoanSteps] = useState<WorkflowDefinitionStep[]>([]);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    setUnits([]);
    setTxns([]);
    setChangeOrders([]);
    setOrders([]);
    setBills([]);
    setEmployees([]);
    setMainSteps([]);
    setLoanSteps([]);
    setProjectMeta(null);
    setBookingInfoByUnitId(new Map());
    setUnitsLoaded(false);
    setTxnsLoaded(false);
    setBookingsLoaded(false);
    setError(null);
    if (!projectId) return;
    const fail = (what: string) => (err: any) => {
      console.error(`Office Today: load ${what} failed`, err);
      setError((prev) => prev || `Couldn't load ${what}. Refresh the page to try again.`);
    };
    const unsubUnits = onSnapshot(
      query(collection(db, 'projects', projectId, 'units'), orderBy('unitNumberLower', 'asc'), limit(5000)),
      (snap) => {
        setUnits(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as any) }))
            .filter((row: any) => row?.isActive !== false)
            .sort((a: any, b: any) => compareUnitNumbers(a?.unitNumber || a?.id, b?.unitNumber || b?.id))
        );
        setUnitsLoaded(true);
      },
      fail('units')
    );
    const unsubTxns = onSnapshot(
      collection(db, 'projects', projectId, 'bankTransactions'),
      (snap) => {
        setTxns(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).filter((row: any) => row?.isActive !== false));
        setTxnsLoaded(true);
      },
      fail('bank receipts')
    );
    const unsubCos = onSnapshot(
      collection(db, 'projects', projectId, 'changeOrders'),
      (snap) => setChangeOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      fail('change orders')
    );
    const unsubOrders = onSnapshot(
      collection(db, 'projects', projectId, 'materialProcurementOrders'),
      (snap) => setOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      () => setOrders([])
    );
    const unsubBills = onSnapshot(
      collection(db, 'projects', projectId, 'materialVendorBills'),
      (snap) => setBills(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      () => setBills([])
    );
    const unsubStaff = onSnapshot(
      collection(db, 'projects', projectId, 'payrollEmployees'),
      (snap) => setEmployees(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      () => setEmployees([])
    );
    // A project points at its workflow definitions; each lives in the project or, failing that, globally.
    const loadDefinition = async (id: string): Promise<WorkflowDefinitionStep[]> => {
      if (!id) return [];
      const own = await getDoc(doc(db, 'projects', projectId, 'workflowDefinitions', id));
      const snap = own.exists() ? own : await getDoc(doc(db, 'officeWorkflowDefinitions', id));
      return snap.exists() ? normalizeDefinitionSteps((snap.data() as any)?.steps || []) : [];
    };
    getDoc(doc(db, 'projects', projectId))
      .then(async (snap) => {
        const meta = snap.exists() ? (snap.data() as any) : null;
        setProjectMeta(meta);
        const [main, loan] = await Promise.all([
          loadDefinition(String(meta?.officeMainWorkflowDefinitionId || '').trim()),
          loadDefinition(String(meta?.officeLoanWorkflowDefinitionId || '').trim()),
        ]);
        setMainSteps(main);
        setLoanSteps(loan);
      })
      .catch(fail('project details'));
    return () => {
      unsubUnits();
      unsubTxns();
      unsubCos();
      unsubOrders();
      unsubBills();
      unsubStaff();
    };
  }, [db, projectId]);

  // Booking docs only change when a unit's active booking changes, so key the load on that.
  const bookingKey = useMemo(
    () =>
      units
        .filter((u) => String(u.activeBookingId || '').trim())
        .map((u) => `${u.id}:${u.activeBookingId}`)
        .join('|'),
    [units]
  );

  useEffect(() => {
    if (!projectId || !unitsLoaded) return;
    let cancelled = false;
    const pairs = bookingKey ? bookingKey.split('|').map((p) => p.split(':')) : [];
    (async () => {
      try {
        const next = new Map<string, TodayBookingInfo>();
        await Promise.all(
          pairs.map(async ([unitId, bookingId]) => {
            const bookingsSnap = await getDocs(query(collection(db, 'projects', projectId, 'units', unitId, 'bookings'), limit(50)));
            const active = bookingsSnap.docs.find((d) => d.id === bookingId);
            const booking = active ? (active.data() as any) : null;
            let saleDeedDone = !!(fieldFromBooking(booking, ['sale_deed_number', 'saleDeedNumber']) || fieldFromBooking(booking, ['sale_deed_date', 'saleDeedDate']));
            if (!saleDeedDone && booking) {
              const uploads = await Promise.all(
                SALE_DEED_UPLOAD_IDS.map((id) => getDoc(doc(db, 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'uploaded', id)))
              );
              saleDeedDone = uploads.some((snap) => {
                if (!snap.exists()) return false;
                const files = (snap.data() as any)?.files;
                return !Array.isArray(files) || files.length > 0;
              });
            }
            let primaryName = String(booking?.customerName || '').trim();
            if (!primaryName && booking) {
              const customersSnap = await getDocs(collection(db, 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'customers'));
              const customers = customersSnap.docs.map((d) => d.data() as any);
              const isPrimary = (c: any) => String(c.role || c.customerType || '').toLowerCase() === 'primary' || c.isPrimary === true;
              const primary = customers.find(isPrimary) || customers.sort((a, b) => toMs(a.createdAt) - toMs(b.createdAt))[0];
              primaryName = primary ? customerNameOf(primary) : '';
            }
            const [mainSnap, loanSnap] = await Promise.all([
              getDoc(doc(db, 'projects', projectId, 'units', unitId, 'cases', bookingId)),
              getDoc(doc(db, 'projects', projectId, 'units', unitId, 'cases', `loan_${bookingId}`)),
            ]);
            const activeCase = (snap: any, type: string): WorkflowCase | null => {
              const data = snap.exists() ? (snap.data() as WorkflowCase) : null;
              return data && String(data.caseType || '') === type && String(data.status || '') === 'active' ? data : null;
            };
            const bookingDate = parseDateLike(booking?.bookingFormDate) || parseDateLike(booking?.createdAt);
            next.set(unitId, {
              bookingId,
              bookingCreatedAtMs: toMs(booking?.createdAt),
              bookingHistoryCount: bookingsSnap.size,
              customerName: primaryName,
              bookingDateMs: bookingDate ? bookingDate.getTime() : 0,
              agreementDone: !!fieldFromBooking(booking, ['sale_agreement_date', 'saleAgreementDate', 'agreement_date', 'agreementDate']),
              saleDeedDone,
              booking,
              mainCase: activeCase(mainSnap, 'main'),
              loanCase: activeCase(loanSnap, 'loan'),
            });
          })
        );
        if (cancelled) return;
        setBookingInfoByUnitId(next);
        setBookingsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Office Today: load bookings failed', err);
        setError((prev) => prev || "Couldn't load booking details. Refresh the page to try again.");
        setBookingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingKey, db, projectId, reloadNonce, unitsLoaded]);

  const loading = !!projectId && (!unitsLoaded || !txnsLoaded || !bookingsLoaded);
  const summary = useMemo(
    () => (loading ? null : buildTodaySummary({ units, bookingInfoByUnitId, txns, projectMeta, changeOrders, orders, mainSteps, loanSteps, includeForms })),
    [bookingInfoByUnitId, changeOrders, includeForms, loading, loanSteps, mainSteps, orders, projectMeta, txns, units]
  );

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);
  const costs = useMemo(() => buildCostSummary(bills, employees), [bills, employees]);
  return { loading, error, summary, costs, projectMeta, units, reload };
}
